/**
 * VaccineTreatmentForm — dedicated editor for vaccine administrations.
 *
 * A vaccine is always administered in consult, always zero-fee, and always a single
 * dose, so this form hard-codes those (no quantity / zero-fee / administered / price
 * controls) and shows only what a vaccine needs: product, dispensing clinic, batch
 * allocation, tag number, injection site, and region-aware booster reminders. It
 * composes the same shared stock hooks/components as `InternalTreatmentForm` and emits
 * the same `internal_administered` API item — the backend derives the vaccination type
 * from the product, so `treatmentCategory` is UI-only.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { z } from 'zod'

import type { ClinicId, ProductType, RegionId } from '@snootsvet/shared'
import {
  clinicIdSchema,
  formatCalendarDate,
  getClinic,
  pluralizeUnit,
  REGIONS_BY_ID,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { FormError } from '@/frontend/components/form/primitives'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { ProductWithStockSearch } from '@/frontend/features/inventory/product-search'
import {
  AllocationProgressBadge,
  type EnrichedAllocationItem,
  StockAllocationBatchSummary,
  StockAllocationResetButton,
  validateStockAllocations,
} from '@/frontend/features/orders/stock-allocation.utils'
import { StockAllocationEditor } from '@/frontend/features/orders/stock-allocation-editor'
import { ReminderFields } from '@/frontend/features/patient/reminder-fields'
import {
  findMatchingVaccineTarget,
  type TreatmentReminderTarget,
  vaccineTargetsForSpecies,
} from '@/frontend/features/patient/reminder-targets'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'

import type { InternalAdministeredItem } from './treatments.types'
import { useFefoAllocation } from './use-fefo-allocation'

/** The product picker only offers vaccines here. */
const VACCINE_PRODUCT_TYPES: ProductType[] = ['vaccine']

/** A vaccine is one dose — quantity is fixed, never shown. */
const VACCINE_QUANTITY = 1

// ---------------------------------------------------------------------------
// Form values & schema
// ---------------------------------------------------------------------------

/** Pre-validation form state — nullable fields for the empty-form case. */
interface VaccineFormValues {
  productId: string | null
  clinicId: ClinicId | null
  allocations: EnrichedAllocationItem[]
  tagNumber: string | null
  injectionSite: string | null
  /** `id` is present for reminders already saved on the server (edit mode) — used to
   *  cancel them via the API when removed from the list. */
  reminders: { id?: string; target: TreatmentReminderTarget | null; dueAt: string | null }[]
  clinicalNotes: string | null
  internalNotes: string | null
}

/** Maps a saved vaccine item back to pre-validation form state for editing. */
function toVaccineFormValues(item: InternalAdministeredItem): VaccineFormValues {
  return {
    productId: item.productId,
    clinicId: item.clinicId,
    allocations: item.allocations,
    tagNumber: item.tagNumber,
    injectionSite: item.injectionSite,
    reminders: item.reminders.map((r) => ({ id: r.id, target: r.target, dueAt: r.dueAt })),
    clinicalNotes: item.clinicalNotes,
    internalNotes: item.internalNotes,
  }
}

const vaccineSchema = z
  .object({
    productId: z.string({ message: 'Please select a vaccine' }).min(1, 'Please select a vaccine'),
    clinicId: clinicIdSchema,
    allocations: z.array(
      z.object({
        batchNumber: z.string().nullable(),
        quantity: z.number().min(0),
      })
    ),
    tagNumber: z.string().nullable(),
    injectionSite: z.string().nullable(),
    reminders: z.array(
      z.object({
        id: z.string().optional(),
        target: z.string().nullable(),
        dueAt: z.string().nullable(),
      })
    ),
    clinicalNotes: z.string().nullable(),
    internalNotes: z.string().nullable(),
  })
  .superRefine((v, ctx) => validateStockAllocations(v.allocations, VACCINE_QUANTITY, ctx))
  .transform((v) => ({
    ...v,
    allocations: v.allocations.filter((a) => a.quantity > 0),
  }))

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface VaccineTreatmentFormProps {
  defaultValues?: InternalAdministeredItem
  /** Clinic the form is mounted in (the consultation's clinic). When set, no picker shows. */
  contextClinicId?: ClinicId
  /** Patient's species — narrows the US disease-list reminder options. */
  patientSpecies: string
  onSave: (item: InternalAdministeredItem) => void
  onCancel: () => void
  editing?: boolean
  /** Whether an async save is in progress — disables submit. */
  saving?: boolean
}

export function VaccineTreatmentForm({
  defaultValues,
  contextClinicId,
  patientSpecies,
  onSave,
  onCancel,
  editing = false,
  saving = false,
}: VaccineTreatmentFormProps) {
  const form = useAppForm({
    defaultValues: defaultValues
      ? toVaccineFormValues({
          ...defaultValues,
          ...(contextClinicId && { clinicId: contextClinicId }),
        })
      : {
          productId: null,
          clinicId: contextClinicId ?? null,
          allocations: [],
          tagNumber: null,
          injectionSite: null,
          reminders: [],
          clinicalNotes: null,
          internalNotes: null,
        },
    validators: {
      onSubmit: ({ value }) => {
        const res = vaccineSchema.safeParse(value)
        if (!res.success) return zodErrorToFieldErrors(res.error)
      },
    },
    onSubmit: ({ value }) => {
      // Re-parse for the validated + transformed output (zero-quantity allocations stripped).
      // The validators.onSubmit pass already guaranteed this succeeds.
      const parsed = vaccineSchema.parse(value)
      onSave({
        type: 'internal_administered',
        treatmentCategory: 'vaccination',
        productId: parsed.productId,
        quantity: VACCINE_QUANTITY,
        clinicId: parsed.clinicId,
        allocations: parsed.allocations,
        zeroFee: true,
        clinicalNotes: parsed.clinicalNotes,
        internalNotes: parsed.internalNotes,
        tagNumber: parsed.tagNumber,
        injectionSite: parsed.injectionSite,
        // Only complete rows (a target + a due date) become reminders — an in-progress
        // row the vet never finished shouldn't be persisted.
        reminders: parsed.reminders
          .filter(
            (r): r is { id?: string; target: string; dueAt: string } => !!r.target && !!r.dueAt
          )
          .map((r) => ({ id: r.id, target: r.target as TreatmentReminderTarget, dueAt: r.dueAt })),
        product: product ?? null,
      })
    },
  })

  const productId = useStore(form.store, (s) => s.values.productId)
  const clinicId = useStore(form.store, (s) => s.values.clinicId)
  const allocations = useStore(form.store, (s) => s.values.allocations)

  const { data: product } = useGetProduct(productId, { include: 'stock' })

  const stockData = useFefoAllocation({
    product,
    clinicId,
    productId,
    quantity: VACCINE_QUANTITY,
    hasInitialAllocations: (defaultValues?.allocations?.length ?? 0) > 0,
    onSuggest: (allocations) => form.setFieldValue('allocations', allocations),
  })

  const { region } = useViewerRegion()
  const viewerDateFmt = useViewerFormat()

  // Reminder targeting is region-specific, resolved from the shot's clinic (falling back to
  // the viewer's region before a clinic is chosen): US offers a fixed disease list, the UK
  // uses the product name as the target.
  const vaccineTargetMode =
    REGIONS_BY_ID[(getClinic(clinicId)?.region as RegionId | undefined) ?? region].vaccineTargetMode

  const vaccineTargets = vaccineTargetsForSpecies(patientSpecies)

  // The UK allows a single reminder per vaccine (one product → one disease); the US allows
  // one per disease the vaccine can cover.
  const maxReminders = vaccineTargetMode === 'disease-list' ? vaccineTargets.length : 1

  // Seed one open booster-reminder row once a vaccine is selected, so the vet doesn't have to
  // click "Add reminder". US pre-fills the disease from the product name; the UK target IS the
  // product name. Guarded to only seed when no editable row exists yet (so it neither
  // duplicates on the product's periodic refetch nor reappears after the vet removes it), and
  // keyed on the product name (stable) rather than the product object (new ref each refetch).
  const productName = product?.name
  useEffect(() => {
    if (!productName) return
    const current = form.getFieldValue('reminders') ?? []
    if (current.some((r) => !r.id)) return
    // Don't seed past the region's cap (e.g. a UK vaccine already has its one reminder).
    if (current.length >= maxReminders) return
    let target: string | null
    if (vaccineTargetMode === 'product-name') {
      target = productName
    } else {
      const matched = findMatchingVaccineTarget(productName, vaccineTargets)
      const taken = new Set(current.map((r) => r.target))
      target = matched && !taken.has(matched) ? matched : null
    }
    form.setFieldValue('reminders', [...current, { target, dueAt: null }])
  }, [productName, vaccineTargets, vaccineTargetMode, maxReminders, form])

  // Only once a vaccine is picked (and loaded) — otherwise the allocation editor reports the
  // empty product as "Cannot dispense product".
  const showStockAllocation = clinicId != null && product != null

  return (
    <form.AppForm>
      <div className="flex flex-col gap-3">
        {/* Dispensing clinic — hidden when mounted in a consultation (clinic is fixed) */}
        {!contextClinicId && (
          <form.AppField name="clinicId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Dispensing Clinic"
                required
                render={({ id, disabled }) => (
                  <ClinicSearchWithDefault
                    id={id}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </form.AppField>
        )}

        {/* Vaccine selector */}
        <form.AppField name="productId">
          {(field) => (
            <FieldWrapper
              field={field}
              label="Vaccine"
              required
              render={({ id, disabled }) => (
                <ProductWithStockSearch
                  id={id}
                  value={product ?? null}
                  onChange={(p) => {
                    field.handleChange(p ? p.id : null)
                    // Reset allocations so FEFO re-suggests for the new product.
                    form.setFieldValue('allocations', [])
                  }}
                  type={VACCINE_PRODUCT_TYPES}
                  clinicId={clinicId ?? undefined}
                  onBlur={field.handleBlur}
                  aria-invalid={field.state.meta.errors.length > 0}
                  disabled={disabled || editing}
                  placeholder="Search vaccines..."
                />
              )}
            />
          )}
        </form.AppField>

        {/* Stock allocation — a vaccine is a single dose from a batch */}
        {showStockAllocation && (
          <form.AppField name="allocations">
            {(field) => {
              const allocated = allocations.reduce((sum, a) => sum + a.quantity, 0)
              return (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 font-medium">Stock Allocation</span>
                      <StockAllocationBatchSummary
                        allocations={allocations}
                        ready={stockData != null}
                        stockStatus={stockData?.stockStatus ?? null}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <AllocationProgressBadge allocated={allocated} required={VACCINE_QUANTITY} />
                      {stockData && (
                        <StockAllocationResetButton
                          onReset={() => field.setValue(stockData.allocations)}
                        />
                      )}
                    </div>
                  </div>
                  {field.state.meta.errors && <FormError errors={field.state.meta.errors} />}
                  <StockAllocationEditor
                    allocations={allocations}
                    onChange={field.setValue}
                    quantity={VACCINE_QUANTITY}
                    availableBatches={stockData?.availableBatches ?? []}
                    stockStatus={stockData?.stockStatus ?? 'no-stock'}
                    clinicId={clinicId ?? undefined}
                    productName={product?.name}
                  />
                </div>
              )
            }}
          </form.AppField>
        )}

        {/* Tag number + injection site */}
        <div className="grid grid-cols-2 gap-4">
          <form.AppField name="tagNumber">
            {(field) => (
              <field.InputField label="Tag number (optional)" placeholder="e.g. RAB-4821" />
            )}
          </form.AppField>
          <form.AppField name="injectionSite">
            {(field) => (
              <field.InputField
                label="Injection site (optional)"
                placeholder="e.g. Left shoulder"
              />
            )}
          </form.AppField>
        </div>

        {/* Booster reminders — already-saved reminders (with a server id) are read-only and can
            only be cancelled (which drops them from form state so they aren't re-submitted
            on save). New rows are created on save. */}
        <form.AppField name="reminders" mode="array">
          {(field) => {
            // Anchor presets to today: a vaccine handled here is given in the current consult,
            // so "now" is the shot date. (The Vaccinations card anchors historical shots to
            // their own clinic-local date.)
            const anchor = new Date()

            return (
              <FieldWrapper
                field={field}
                label="Booster reminders"
                render={() => (
                  <div className="flex flex-col gap-2">
                    {field.state.value.map((row, i) => {
                      // Persisted reminders (server id) are read-only — show target + due date.
                      if (row.id) {
                        return (
                          <div key={row.id} className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground">
                              {row.target ?? '—'}
                              {row.dueAt
                                ? ` · Due ${formatCalendarDate(row.dueAt, viewerDateFmt)}`
                                : ''}
                            </span>
                            <ConfirmButton
                              type="button"
                              variant="ghost"
                              heading="Remove reminder?"
                              description={`The ${row.target} booster reminder will be removed when you save this treatment.`}
                              confirmLabel="Remove reminder"
                              dialogColor="destructive"
                              // Form state only — dropping the row here excludes it from the
                              // save payload. Abandoning the form leaves the reminder untouched.
                              onConfirm={() => field.removeValue(i)}
                            >
                              Remove
                            </ConfirmButton>
                          </div>
                        )
                      }

                      // New rows (no id) are editable. For the US disease dropdown, a disease
                      // already picked by another row can't be picked again — filter it out (but
                      // keep this row's own selection so its Select shows a label). The UK has no
                      // dropdown at all: the target is the product name, seeded onto the row.
                      const chosenElsewhere = new Set(
                        field.state.value
                          .filter((_, j) => j !== i)
                          .map((r) => r.target)
                          .filter((t): t is TreatmentReminderTarget => t != null)
                      )
                      const rowOptions =
                        vaccineTargetMode === 'product-name'
                          ? undefined
                          : vaccineTargets.filter((t) => !chosenElsewhere.has(t))
                      return (
                        // biome-ignore lint/suspicious/noArrayIndexKey: new rows have no stable identity
                        <div key={i} className="flex flex-wrap items-center gap-2">
                          <ReminderFields
                            options={rowOptions}
                            target={row.target}
                            dueAt={row.dueAt}
                            anchor={anchor}
                            onTargetChange={(target) => field.replaceValue(i, { ...row, target })}
                            onDueAtChange={(dueAt) => field.replaceValue(i, { ...row, dueAt })}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => field.removeValue(i)}
                          >
                            Remove
                          </Button>
                        </div>
                      )
                    })}
                    {/* Hidden once at the cap (US: one row per disease; UK: one per vaccine). */}
                    {field.state.value.length < maxReminders && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          field.pushValue({
                            target:
                              vaccineTargetMode === 'product-name' ? (productName ?? null) : null,
                            dueAt: null,
                          })
                        }
                      >
                        Add reminder
                      </Button>
                    )}
                  </div>
                )}
              />
            )
          }}
        </form.AppField>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <form.ConfirmSubmitButton
            type="button"
            disabled={saving || stockData?.stockStatus === 'no-stock'}
            heading="Confirm vaccine"
            description={
              <div className="flex flex-col gap-2">
                <p>
                  This vaccine will be administered in consult and stock will be deducted
                  immediately. It can be edited or deleted within 7 days while unpaid.
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4">
                  <dt className="font-medium text-foreground">Vaccine:</dt>
                  <dd>
                    {product?.name ?? 'Unknown'} — {VACCINE_QUANTITY} ×{' '}
                    {pluralizeUnit(product?.unit ?? null, VACCINE_QUANTITY)}
                  </dd>
                  {allocations.length > 0 && (
                    <>
                      <dt className="font-medium text-foreground">Batch:</dt>
                      <dd>
                        {allocations
                          .map((a) => `${a.batchNumber ?? 'No batch number'} × ${a.quantity}`)
                          .join(', ')}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            }
            confirmLabel="Save"
          >
            {saving ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </form.ConfirmSubmitButton>
        </div>
      </div>
    </form.AppForm>
  )
}
